const C1_QUIESCENT = 95
const C2_QUIESCENT = 0.425
const SOLAR_CONSTANT = 0.33
const LBS_TO_LITRES_FACTOR = 2.2026432
const STANDARD_PRESSURE_HG = 29.921259
const CELSIUS_TO_FAHRENHEIT_FACTOR = 1.8
const EVAPORATION_PER_PERSON_LBS_HOUR = 0.45
const SQUARE_METERS_TO_SQUARE_FEET_FACTOR = 10.76

export const MACHINE_TYPE_STANDARD = 'MACHINE_TYPE_STANDARD'
export const MACHINE_TYPE_HEATER = 'MACHINE_TYPE_HEATER'

export const OPERATION_HEAT = 'OPERATION_HEAT'
export const OPERATION_COOL = 'OPERATION_COOL'
export const OPERATION_HOT_WATER = 'OPERATION_HOT_WATER'

const convertToDecimal = obj => {
    Object.keys(obj).forEach(key => {
        obj[key] = parseFloat(obj[key].toFixed(1));
    });

    return obj;
}

const celsiusToFahrenheit = celsius => {
    return celsius * CELSIUS_TO_FAHRENHEIT_FACTOR + 32;
}

const getSurfaceArea = (surfaceAreaInMeters, constructionType, overflowFactor) => {
    let area = surfaceAreaInMeters

    if (constructionType === 'In-ground overflow') {

        /*
         Estimate total area of water by assuming the pool is square,
         finding the side length, adding an extra length to it,
         then squaring the whole thing to find the new area.
         We start at 3 feet (0.914)
        */

        const overflow = 0.914 * overflowFactor

        area = Math.pow(Math.sqrt(surfaceAreaInMeters) + overflow, 2)
    }

    return area;
};

const vapourPressure = (temperature, pressure) => {
    const T = temperature + 273.15;
    const E = 647.27 - T;
    const A = 3.2437814;
    const B = 5.86826e-3;
    const C = 1.1702379e-8;
    const D = 2.1878462e-3;
    const O = (-E / T) * (A + B * E + C * E * E * E) / (1 + D * E);
    const PW = 218.167 * Math.pow(10, O);
    const P = PW * pressure;
    const Y = 1075.16 - (temperature * CELSIUS_TO_FAHRENHEIT_FACTOR * 0.583333);

    return {
        vapourPressureOfWaterInHg: P,
        latentHeatOfWaterBtu: Y,
    }
};

const calculateDewpointTemperature = (relativeHumidity, temperature) => {
    const x = 1 - (relativeHumidity / 100);
    const A = (14.55 + 0.114 * temperature) * x;
    const B = Math.pow((2.5 + 0.007 * temperature) * x, 3);
    const K = (15.9 + 0.117 * temperature) * Math.pow(x, 14);

    return temperature - (A + B + K);
};

const getSunIntensityFactor = intensity => {
    return {
        High: 0.93,
        Medium: 0.85,
        Low: 0.45,
    }[intensity]
}

const getGroundLossFactor = (waterTableHeight, waterCharacteristics) => {
    return {
        High: waterCharacteristics === 'Still' ? 2 : 3.5,
        Low: 1,
    }[waterTableHeight]
}

const poolUsageModifiers = poolUsage => {
    return {
        Private: {
            ACTIVITY_LOSS: 1.9,
            OVERFLOW_FACTOR: 0.6,
        },
        Commercial: {
            ACTIVITY_LOSS: 2.5,
            OVERFLOW_FACTOR: 0.7,
        },
        Spa: {
            ACTIVITY_LOSS: 3.5,
            OVERFLOW_FACTOR: 1,
        },
    }[poolUsage]
}

const getPoolRadiation = requiredPoolTempInC => {
    return (5.67e-8) * Math.pow(273 + requiredPoolTempInC - 33, 4)
}

const getSupplyHz = hz => {
    return {
        50: 1,
        60: 1.15,
    }[hz]
}

const getDiffs = (machineType = 'MACHINE_TYPE_STANDARD') => {
    return {
        OPERATION_HOT_WATER: {
            CTDiff: 4,
            ETDiff: 10,
        },
        OPERATION_HEAT: {
            CTDiff: 8,
            ETDiff: 10,
        },
        MACHINE_TYPE_STANDARD: {
            CTDiff: 10,
            ETDiff: 12,
        },
    }[machineType]
}

const generatePoly = (matrix, TE, TC) => {
    return matrix[0] + (matrix[1] * TE) + (matrix[2] * TC) + (matrix[3] * Math.pow(TE, 2)) + (matrix[4] * TE * TC) + (matrix[5] * Math.pow(TC, 2)) + (matrix[6] * Math.pow(TE, 3)) + (matrix[7] * Math.pow(TE, 2) * TC) + (matrix[8] * TE * Math.pow(TC, 2)) + (matrix[9] * Math.pow(TC, 3));
}

const reciprocalPolynomialFittings = (refrigerant, btuDuty, supplyHz) => {
    const eer = 10.478

    let RFactor, PFactor

    switch (refrigerant) {
    case 'r22':
        RFactor = 0.921
        PFactor = 0.9
        break;
    case 'r407c':
        RFactor = 0.839
        PFactor = 0.85
        break;
    case 'r410a':
        RFactor = 0.839
        PFactor = 0.85
        break;
    case 'r32':
        // eslint-disable-next-line line-comment-position
        RFactor = 0.839 // was 0.9
        // eslint-disable-next-line line-comment-position
        PFactor = 0.85 // was 0.8
        break;
    default:
        RFactor = 0.6261
        PFactor = 0.6
        break;
    }

    const dutyPerCycle = (btuDuty / 26667) * supplyHz

    const efficiencyRatio = eer / 10.478

    return {
        RFactor,
        PFactor,
        dutyPerCycle,
        efficiencyRatio,
        cap: [
            -6432.837402,
            140.5539093,
            615.6174316,
            4.907305241,
            5.306796074,
            -6.810205936,
            0.041762754,
            -0.028634408,
            -0.030115779,
            0.020463327,
        ],
        pow: [
            3445.119385,
            -18.82971954,
            -65.46955872,
            -0.565910757,
            0.522111118,
            0.645741999,
            -0.000504118,
            0.003784601,
            -0.001121658,
            -0.002143472,
        ],
        cur: [
            -12.58082294,
            -0.325771034,
            0.437524974,
            0.001622444,
            0.003698605,
            -0.002877691,
            0.0000222616,
            -0.0000271586,
            -0.00000321745,
            0.00000564943,
        ],
        mas: [
            -55.41199112,
            0.449531317,
            6.190982819,
            0.035069022,
            0.076685421,
            -0.062556855,
            0.000469639,
            -0.000134831,
            -0.000313781,
            0.000166525,
        ],
    }
};

const scrollPolynomialFittings = (refrigerant, btuDuty, supplyHz) => {
    const eer = 11.8;

    if (refrigerant === 'r22') {
        return {
            RFactor: 1,
            PFactor: 1,
            dutyPerCycle: (btuDuty / 160000) * supplyHz,
            efficiencyRatio: eer / 11.8,
            cap: [
                95400,
                2070,
                -594,
                27.3,
                -15.9,
                6.95,
                0.114,
                -0.246,
                0.139,
                -0.0511,
            ],
            pow: [
                -4850,
                -174,
                301,
                -2.02,
                4.7,
                -3.26,
                -0.0108,
                0.0273,
                -0.0294,
                0.0149,
            ],
            cur: [
                11.3,
                0.0992,
                -0.0331,
                0.00151,
                -0.00211,
                0.000526,
                0.0000131,
                -0.0000206,
                0.0000125,
                0.00000175,
            ],
            mas: [
                1060,
                23,
                -8.94,
                0.296,
                -0.23,
                0.154,
                0.00139,
                -0.00288,
                0.00245,
                -0.000963,
            ],
        }
    }

    if (refrigerant === 'r407c' || refrigerant === 'r410a') {
        return {
            RFactor: 0.93125,
            PFactor: 1.0958,
            dutyPerCycle: (btuDuty / 149000) * supplyHz,
            efficiencyRatio: eer / 10.8,
            cap: [
                82500,
                1850,
                -37.6,
                22.6,
                1.17,
                -3.81,
                0.0904,
                -0.0771,
                -0.0355,
                0.0082,
            ],
            pow: [
                -496,
                -9.6,
                120,
                0.27,
                0.364,
                -0.968,
                0.00165,
                -0.00351,
                -0.00183,
                0.00572,
            ],
            cur: [
                3.05,
                0.0258,
                0.246,
                0.00108,
                -0.00084,
                -0.00217,
                0.00000255,
                -0.0000129,
                0.00000708,
                0.00000978,
            ],
            mas: [
                819,
                17.8,
                -0.296,
                0.155,
                0.024,
                -0.0021,
                0.000863,
                0.000103,
                0.00000598,
                -0.000114,
            ],
        }
    }

    return {
        RFactor: 0.68125,
        PFactor: 0.70438,
        dutyPerCycle: (btuDuty / 109000) * supplyHz,
        efficiencyRatio: eer / 11.6,
        cap: [
            63800,
            1390,
            -397,
            18.2,
            -10.6,
            4.65,
            0.0762,
            -0.165,
            0.0928,
            -0.0342,
        ],
        pow: [
            -3270,
            -117,
            203,
            -1.36,
            3.17,
            -2.2,
            -0.00728,
            0.0184,
            -0.0198,
            0.01,
        ],
        cur: [
            8.8,
            0.0773,
            -0.0258,
            0.00117,
            -0.00164,
            0.00041,
            0.0000102,
            -0.000016,
            0.00000972,
            0.00000136,
        ],
        mas: [
            786,
            18.5,
            -9,
            0.225,
            -0.241,
            0.155,
            0.00107,
            -0.00244,
            0.00233,
            -0.000886,
        ],
    }
}

export const heatLossCalculator = ({
    surfaceAreaInMeters,
    constructionType,
    requiredPoolTempInC,
    altitudeAboveSeaLevelInMeters,
    airDryBulbTempInC,
    relativeHumidityAsPercentage,
    windSpeedInKph,
    poolUsage,
    groundTempInC,
    waterPumpKw,
    pumpRunningHours,
    hoursUsed,
    numberOfPeople,
    hoursUncovered,
    hoursOfSun,
    sunIntensity,
    waterCharacteristics,
    waterTableHeight,
    safetyMargin,
}) => {
    const { ACTIVITY_LOSS, OVERFLOW_FACTOR } = poolUsageModifiers(poolUsage)
    const surfaceArea = getSurfaceArea(surfaceAreaInMeters, constructionType, OVERFLOW_FACTOR)
    const surfaceAreaInFeet = surfaceArea * SQUARE_METERS_TO_SQUARE_FEET_FACTOR

    const { vapourPressureOfWaterInHg: PX, latentHeatOfWaterBtu: Y1 } = vapourPressure(requiredPoolTempInC, STANDARD_PRESSURE_HG)

    const pressureChangeRatio = 1 + (altitudeAboveSeaLevelInMeters / 18000)
    const windSpeedInFeetPerMin = windSpeedInKph * 55

    const standardPressureWithAltitude = STANDARD_PRESSURE_HG / (pressureChangeRatio * pressureChangeRatio)

    const temperatureOfBoundaryLayer = (requiredPoolTempInC + airDryBulbTempInC) / 2

    const relativeHumidityBoundary = (relativeHumidityAsPercentage + 100) / 2

    const dewpointTemperature = calculateDewpointTemperature(
        relativeHumidityBoundary,
        temperatureOfBoundaryLayer,
    )

    const dewpointTemperatureNormalPoolHallAir = calculateDewpointTemperature(
        relativeHumidityAsPercentage,
        airDryBulbTempInC,
    )

    const { vapourPressureOfWaterInHg: PA } = vapourPressure(dewpointTemperature, standardPressureWithAltitude)

    const evaporationLbsHourFromStillPool = (surfaceAreaInFeet * (C1_QUIESCENT + (C2_QUIESCENT * windSpeedInFeetPerMin))) / Y1 * (PX - PA)

    let heatLossInKwh = (evaporationLbsHourFromStillPool * Y1) / 3412
    const activityEvaporationLoss = evaporationLbsHourFromStillPool * (ACTIVITY_LOSS - 1) * 0.6
    const groundLoss = (((requiredPoolTempInC - groundTempInC) * CELSIUS_TO_FAHRENHEIT_FACTOR) / 3.2) * ((((surfaceAreaInFeet + (15 * (Math.sqrt(2 * surfaceAreaInFeet))) * 1.1)) / 3412) * getGroundLossFactor(waterTableHeight, waterCharacteristics))
    const averagePumpKw = waterPumpKw * pumpRunningHours / 24
    const heatingFromPumpAndPoolUse = averagePumpKw + (((surfaceAreaInFeet / 100) * 0.1 * hoursUsed) / 24)

    const totalEvaporation = activityEvaporationLoss + evaporationLbsHourFromStillPool + (numberOfPeople * EVAPORATION_PER_PERSON_LBS_HOUR / 2)
    const peakEvaporation = (evaporationLbsHourFromStillPool * ACTIVITY_LOSS) + (numberOfPeople * EVAPORATION_PER_PERSON_LBS_HOUR)

    const heatLossWithCover = heatLossInKwh * 0.25
    const peakActivityEvaporationInLitres = peakEvaporation / LBS_TO_LITRES_FACTOR

    let averageEvaporationLbsHour = (((0.25 * (24 - hoursUncovered) * evaporationLbsHourFromStillPool) + (evaporationLbsHourFromStillPool * hoursUncovered) + (activityEvaporationLoss * hoursUsed) + ((numberOfPeople * EVAPORATION_PER_PERSON_LBS_HOUR / 2) * hoursUsed))) / 24

    if (evaporationLbsHourFromStillPool < 0) {
        averageEvaporationLbsHour = averageEvaporationLbsHour / 2
        heatLossInKwh = heatLossInKwh / 2
    }

    const averageOccupiedEvaporationInLitres = totalEvaporation / LBS_TO_LITRES_FACTOR
    const averageEvaporationCycleInLitres = averageEvaporationLbsHour / LBS_TO_LITRES_FACTOR
    const quiescentEvaporationInLitres = evaporationLbsHourFromStillPool / LBS_TO_LITRES_FACTOR

    let totalLoss = (((heatLossWithCover * (24 - hoursUncovered)) + (heatLossInKwh * hoursUncovered)) / 24) + groundLoss + ((((activityEvaporationLoss * Y1) / 3412) * hoursUsed) / 24)

    let poolRadiation = getPoolRadiation(requiredPoolTempInC)
    const radiationDuration = airDryBulbTempInC > 33 ? (17 - hoursOfSun) : (15 - hoursOfSun)
    const heatGainedPerUnitAreaPerHourDueToSun = SOLAR_CONSTANT * getSunIntensityFactor(sunIntensity) * hoursOfSun / 24

    if (hoursUncovered < 24) {
        poolRadiation = poolRadiation * 0.25
    }

    const energyRadiatedOverSurfaceInKw = surfaceArea * poolRadiation * radiationDuration / 1000

    totalLoss = totalLoss + (energyRadiatedOverSurfaceInKw / 24)

    const peakLossesFromEvaporation = ((heatLossInKwh * ACTIVITY_LOSS) + groundLoss)

    const gainsFromSun = (surfaceArea * heatGainedPerUnitAreaPerHourDueToSun) + heatingFromPumpAndPoolUse

    const netHeatLoss = totalLoss - gainsFromSun

    const dailyHeatLoss = netHeatLoss * 24

    const dailyTotalEvaporation = parseFloat((averageEvaporationCycleInLitres * 24).toFixed(0))

    const estimateOverflowArea = surfaceArea - surfaceAreaInMeters

    const heatLossValues = {
        peakActivityEvaporationInLitres,
        averageOccupiedEvaporationInLitres,
        averageEvaporationCycleInLitres,
        quiescentEvaporationInLitres,
        dewpointTemperatureNormalPoolHallAir,
        peakLossesFromEvaporation,
        groundLoss,
        energyRadiatedOverSurfaceInKw,
        totalLoss,
        gainsFromSun,
        netHeatLoss,
        netLossWithSafetyMargin: netHeatLoss * safetyMargin,
        dailyHeatLoss,
        dailyTotalEvaporation,
        estimateOverflowArea,
    };

    return convertToDecimal(heatLossValues);
};

// eslint-disable-next-line max-params
const analyseModel = ({
    poolLoss,
    supplyHz,
    relativeHumidityFactor,
    CTDiff,
    ETDiff,
    airDryBulbTempInC,
    requiredPoolTempInC,
    operation,
    machineType,
    hotWater,
    pumpRunningHours,
    safetyMargin,
}, {
    performanceFactor,
    btuDuty,
    refrigerant,
    maximumEvaporationTemperature,
    minimumCompressorTemperature,
    compressorType,
    fanPowerKw,
}) => {
    refrigerant = refrigerant.toLowerCase()

    const poolLossWithSafetyMargin = poolLoss * safetyMargin

    let evaporationTemperature = (airDryBulbTempInC + relativeHumidityFactor + 6.8) - (ETDiff * performanceFactor)

    let compressorTemperature = requiredPoolTempInC + CTDiff + (evaporationTemperature / 2)

    if (operation === OPERATION_COOL && machineType === MACHINE_TYPE_HEATER) {
        evaporationTemperature = requiredPoolTempInC - (ETDiff * performanceFactor)
        compressorTemperature = airDryBulbTempInC + CTDiff + (evaporationTemperature / 2)

        if (evaporationTemperature > maximumEvaporationTemperature) {
            evaporationTemperature = maximumEvaporationTemperature
        }

        if (compressorTemperature < minimumCompressorTemperature) {
            compressorTemperature = minimumCompressorTemperature
        }
    }

    if (operation === OPERATION_HEAT && (machineType === MACHINE_TYPE_HEATER || hotWater)) {
        if (evaporationTemperature > maximumEvaporationTemperature) {
            evaporationTemperature = maximumEvaporationTemperature
        }

        if (compressorTemperature < minimumCompressorTemperature) {
            compressorTemperature = minimumCompressorTemperature
        }
    }

    if (operation === OPERATION_HEAT && machineType === MACHINE_TYPE_STANDARD) {
        if (evaporationTemperature > maximumEvaporationTemperature) {
            evaporationTemperature = maximumEvaporationTemperature
        }

        if (compressorTemperature < minimumCompressorTemperature) {
            compressorTemperature = minimumCompressorTemperature
        }
    }

    const evaporationTemperatureInFahrenheit = celsiusToFahrenheit(evaporationTemperature)
    const compressorTemperatureInFahrenheit = celsiusToFahrenheit(compressorTemperature)

    const {
        cap, pow, dutyPerCycle, efficiencyRatio, RFactor, PFactor,
    } = compressorType === 'Recip' || compressorType === 'Rotary' || compressorType === 'Inverterrotary' ? reciprocalPolynomialFittings(refrigerant, btuDuty, supplyHz) : scrollPolynomialFittings(refrigerant, btuDuty, supplyHz)

    const capPoly = generatePoly(cap, evaporationTemperatureInFahrenheit, compressorTemperatureInFahrenheit)
    const powPoly = generatePoly(pow, evaporationTemperatureInFahrenheit, compressorTemperatureInFahrenheit)
    const factor = 0.9

    const defrost = evaporationTemperature <= -2 ? 0.9 : 1
    const dutykW = RFactor * capPoly * dutyPerCycle / 3412
    const powerInput = PFactor * powPoly * dutyPerCycle / (1000 * efficiencyRatio)

    const heatPumpDuty = factor * dutykW + powerInput
    const heatPumpInput = powerInput + (((fanPowerKw * 1000) * 1.2) / 1000)

    let machinekW = heatPumpDuty * defrost;

    if (operation === OPERATION_COOL && machineType === MACHINE_TYPE_HEATER) {
        machinekW = dutykW
    }

    let numberOfMachinesRequired = Math.ceil((24 * poolLossWithSafetyMargin / (machinekW * pumpRunningHours)))

    if (numberOfMachinesRequired < (poolLossWithSafetyMargin / (machinekW * (pumpRunningHours / 24))) * 0.95) {
        numberOfMachinesRequired++
    }

    const dayDutyPercentage = (100 * (numberOfMachinesRequired * machinekW * pumpRunningHours) / (24 * poolLossWithSafetyMargin)).toFixed(1)

    const co2elec = poolLossWithSafetyMargin * 24 * 0.43
    const co2oil = poolLossWithSafetyMargin * 24 * 0.24 * 1.17647
    const co2gas = poolLossWithSafetyMargin * 24 * 0.19 * 1.282

    const hoursRun = (poolLossWithSafetyMargin * 24 / machinekW) / numberOfMachinesRequired

    let reserveDuty = ((100 * (numberOfMachinesRequired * machinekW * pumpRunningHours) / (24 * poolLossWithSafetyMargin)) - 100)

    if (reserveDuty <= 0) {
        reserveDuty = 0
    }

    let analyseValues = {
        netLoss: poolLoss,
        netLossWithSafetyMargin: poolLossWithSafetyMargin,
        safetyMargin,
        unitsRequired: numberOfMachinesRequired > 0 ? numberOfMachinesRequired : 1,
        dayDutyPercentage: parseFloat(dayDutyPercentage),
        heatDuty: machinekW,
        allowedDuty: numberOfMachinesRequired * machinekW * pumpRunningHours,
        cop: machinekW / heatPumpInput,
        co2kWh: 0.43 / (machinekW / heatPumpInput),
        co2day: numberOfMachinesRequired * 0.43 * heatPumpInput * pumpRunningHours / (100 * (numberOfMachinesRequired * machinekW * pumpRunningHours) / (24 * poolLoss) / 100),
        co2elec,
        co2oil,
        co2gas,
        hoursRun,
        reserveDuty,
        powerInput,
    }

    analyseValues = convertToDecimal(analyseValues);

    return {...analyseValues, fanPowerKw: parseFloat(fanPowerKw.toFixed(2))};
}

export const productAnalyser = ({
    poolLoss,
    products,
    operation,
    hotWater,
    powerSupply,
    relativeHumidityAsPercentage,
    airDryBulbTempInC,
    requiredPoolTempInC,
    pumpRunningHours,
    safetyMargin,
}) => {
    const supplyHz = getSupplyHz(powerSupply)

    const relativeHumidityFactor = calculateDewpointTemperature(relativeHumidityAsPercentage, 0)

    return products.map(model => {
        const machineType = model?.maths?.machineTypeOperation;
        const { CTDiff, ETDiff } = getDiffs(machineType);
        const minPoolTempConditionals = model?.maths?.minPoolTempConditionals;

        if (minPoolTempConditionals) {

            let results = [];

            // Work out valid pool temperatures
            minPoolTempConditionals.some(temp => {
                if (requiredPoolTempInC < temp.minimumPoolTemp) {
                    results.push(temp);
                }
            });

            // Get all the temperatures to compare
            const getPoolTemps = results.map(temp => temp.minimumPoolTemp);

            // Find the lowest pool temperature value
            const getMinPoolTemps = Math.min(...getPoolTemps);

            // Filter results that meet this criteria
            const filteredPoolResults = results.filter(temp => temp.minimumPoolTemp === getMinPoolTemps);

            // Filter our new results to find valid air bulb temperatures - only return ones that have a value
            results = filteredPoolResults.filter(temp => {
                if (temp.airDryBulbTemp !== null) {
                    return airDryBulbTempInC < temp.airDryBulbTemp
                }
            });

            // If we have results then continue to work out the best match for the air dry bulb based on the pool temperature results
            if (results.length) {
                // Find the air bulb temperatures
                const getAirBulbTemps = results.map(temp => temp.airDryBulbTemp);

                // Get the lowest value that matches
                const getMinAirBulbTemps = Math.min(...getAirBulbTemps);

                // Return best matched result based on pool temp and then the air bulb temp
                const conditionalResult = results.filter(temp => temp.airDryBulbTemp === getMinAirBulbTemps)[0];

                // Update our maths values with the new performance factors and btu duty
                model.maths.performanceFactor = conditionalResult.performanceFactor;
                model.maths.btuDuty = conditionalResult.btuDuty;
            } else {
                // Return and update our maths values with the new performance factors and btu duty based on best result from the temperature list
                model.maths.performanceFactor = filteredPoolResults[0].performanceFactor;
                model.maths.btuDuty = filteredPoolResults[0].btuDuty;
            }
        }

        return {
            model,
            analysis: analyseModel({
                poolLoss,
                safetyMargin,
                supplyHz,
                relativeHumidityFactor,
                CTDiff,
                ETDiff,
                machineType,
                operation,
                airDryBulbTempInC,
                requiredPoolTempInC,
                pumpRunningHours,
                hotWater,
            }, model.maths),
        }
    });
}
